import { useTranslation } from "react-i18next";
import { colors } from "../../../data/colors";
import { stdObject } from "../../../data/types/stdObject";
import { toPrice } from "../../../utils/toPrice";
import WriteInput from "../../componentModal/WriteInput";
import NOPIC from "../../../assets/nopic.svg";
import { emptys, mainConfig, purchasesCodes } from "../../../data/main";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PendingIcon from "@mui/icons-material/Pending";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { SetStateAction, useMemo, useRef, useState } from "react";
import { getPricesList } from "../../../utils/getPricesList";
import { useDocContext, useMainContext } from "../../../context/MainContext";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { fieldNotAllowed } from "../../../data/fieldNotAllowed";
import writeBatchInfo from "./writeBatchInfo";
import QuickSearch from "./QuickSearch";
import CommissionBar from "./CommissionBar";
import { updateCheckStock } from "../../../screens/MainFrame/functions/document/updateCheckStock";

const itemsCell = [
  { type: "STOCK", width: 30, rowspan: 2, align: "center" },
  { type: "IMG", width: 50, rowspan: 2 },
  { type: "NOM", label: "p.item", colspan: true, line1: true },
  { type: "COL", label: "c.COL", width: 100, hidden: true, line2: true },
  {
    type: "MOD",
    label: "p.status",
    width: 100,
    inputWidth: 60,
    align: "center",
    line2: true,
  },
  {
    type: "CAN",
    label: "c.CAN",
    width: 100,
    inputWidth: 60,
    align: "center",
    line2: true,
  },
  {
    type: "PRE",
    label: "p.price",
    width: 50,
    inputWidth: 80,
    align: "right",
    line2: true,
  },
  {
    type: "DES",
    label: "d.DES",
    width: 100,
    inputWidth: 50,
    align: "center",
    line2: true,
  },
  {
    type: "IMP",
    label: "c.IMP",
    width: 100,
    align: "center",
    inputStyle: { width: 30 },
    line2: true,
  },
  { type: "TOT", label: "c.TOT", width: 100, align: "right", line2: true },
  { type: "MORE", width: 20, align: "right", rowspan: 2 },
  { type: "DEL", width: 20, align: "right", rowspan: 2 },
];

export default function ItemsTable({
  type,
  items,
  minModalWidth,
  mainColor,
}: {
  type: string;
  items: Array<stdObject>;
  minModalWidth: boolean;
  mainColor: string;
}) {
  const { t } = useTranslation();
  const searchRef = useRef<HTMLInputElement>(null);
  const { info, docActive } = useDocContext();
  const {
    docValues,
    setDocValues,
    setMsg,
    setModalData,
    setWindowValues,
    openItemOptions,
    insertNewItem,
    updateDocData,
    logoutAction,
  } = useMainContext();
  const [inputCode, setInputCode] = useState("");
  const [isDown, setIsDown] = useState(true);
  const [resList, setResList] = useState<stdObject[]>([]);
  const [selectPos, setSelectPos] = useState<number>(0);
  const user: any = useSelector((state: RootState) => state.data.user);
  const allTaxes: any = useSelector((state: RootState) => state.data.imp);
  const taxesList: any = useMemo(
    () => allTaxes.filter((e: stdObject) => e.CODIMP === user.CODPAI),
    [allTaxes, user.CODPAI],
  );
  const itemsList: any = useSelector((state: RootState) => state.data.art);
  const pricesRates: any = useSelector((state: RootState) => state.data.tar);

  const getPosition = () => {
    if (searchRef.current) {
      const rect = searchRef.current.getBoundingClientRect();
      const tempIsDown = Number(rect.top) < window.innerHeight / 2;
      if (tempIsDown !== isDown) {
        setIsDown(tempIsDown);
      }
    }
  };

  const cleanInput = () => setInputCode("");

  const writeContent = (
    rowData: stdObject,
    itemData: stdObject,
    index: number,
  ) => {
    const suffix = Object.keys(itemData)[0].slice(-3);
    switch (rowData.type) {
      case "MOD":
        return (
          <WriteInput
            data={{
              type: "select",
              value: itemData[`${rowData.type}${suffix}`],
              inputStyle: rowData.inputStyle,
              options: [
                {
                  label: "",
                  value: 0,
                },
                {
                  label: t("p.damaged"),
                  value: 1,
                },
              ],
              style: {
                display: "flex",
                justifyContent: "center",
              },
              onChange: (v: { target: { value: SetStateAction<string> } }) =>
                itemData.onChange(Number(v.target.value), rowData.type),
            }}
          />
        );
      case "MORE":
        return (
          <>
            <CommissionBar
              comm={docValues.commissions}
              line={itemData[`LIN${suffix}`]}
            />
            <PendingIcon
              onClick={() => openItemOptions(index, itemData)}
              style={{ color: colors.darkGrey, cursor: "pointer" }}
            />
          </>
        );
      case "DEL":
        return (
          <DeleteForeverIcon
            onClick={itemData.delete}
            style={{ color: colors.red, cursor: "pointer" }}
          />
        );
      case "NOM":
        return (
          <div>
            <span style={{ fontWeight: "bolder" }}>
              {itemData[`ART${suffix}`]}
            </span>
            <div>{itemData[`NOM${suffix}`]}</div>
            {itemData[`OBS${suffix}`] !== "" && (
              <div>{itemData[`OBS${suffix}`]}</div>
            )}
            {!!itemData.BATCHES?.length && writeBatchInfo(itemData.BATCHES)}
          </div>
        );
      case "STOCK":
        const isSndStock =
          info.type === "TRA" &&
          user.CODSUC !== info.data[`SER${info.type}`].value;

        const stockQty = isSndStock
          ? itemData[`PEN2${suffix}`]
          : itemData[`PEN${suffix}`];

        const validValue =
          itemData[`CAN${suffix}`] > 0 && itemData[`CAN${suffix}`] === stockQty
            ? 2
            : stockQty <= 0
              ? 0
              : 1;
        const IconFrame =
          validValue === 2
            ? CheckBoxIcon
            : validValue
              ? IndeterminateCheckBoxIcon
              : CheckBoxOutlineBlankIcon;
        const iconColor = !docActive.items
          ? colors.grey
          : validValue === 2
            ? colors.green
            : validValue
              ? colors.orange
              : colors.grey;
        return (
          <IconFrame
            style={{ color: iconColor }}
            onClick={
              docActive.items
                ? () =>
                    updateCheckStock(index, isSndStock, false, {
                      user,
                      items: itemsList,
                      docValues,
                      setDocValues,
                      setMsg,
                      setModalData,
                      setWindowValues,
                      logoutAction,
                    })
                : () => {}
            }
          />
        );
      case "IMG":
        const imageData = itemsList[itemData[`ART${suffix}`]];

        const itemImage =
          imageData && imageData.IMGART !== ""
            ? `${mainConfig.url}/images/ART/small/${
                imageData.IMGUART !== ""
                  ? imageData.IMGUART
                  : itemData[`ART${suffix}`]
              }.${imageData.IMGART}?v=${imageData.UPTART}`
            : NOPIC;
        return (
          <div
            className="listImage"
            style={{ width: 35, height: 25, margin: 3 }}
          >
            <img
              src={itemImage}
              onError={(e: any) => (e.target.src = NOPIC)}
              loading="lazy"
              alt={itemsList[itemData[`ART${suffix}`]]?.CODART}
            />
          </div>
        );
      default:
        let defaultValue = itemData[`${rowData.type}${suffix}`];
        if (
          ["CAN", "DES", "PRE", "TOT"].includes(rowData.type) &&
          (isNaN(defaultValue) || emptys.includes(defaultValue))
        ) {
          defaultValue = "0";
        }
        switch (rowData.type) {
          case "PRE":
            return docActive.items ? (
              <WriteInput
                data={{
                  type: purchasesCodes.includes(type) ? "number" : "select",
                  value: defaultValue,
                  active: user[`${type}US1`],
                  options: getPricesList({
                    type,
                    value: defaultValue,
                    pricesList:
                      itemsList[itemData[`ART${suffix}`]]?.PREART[
                        user.CODPAI
                      ] ?? [],
                    pricesRates,
                    label: t("p.another"),
                    user,
                  }),
                  innerStyle: { width: 70, textAlign: "right" },
                  onBlur: (v: { target: { value: SetStateAction<string> } }) =>
                    itemData.onChange(v.target.value, rowData.type, {
                      blur: true,
                    }),
                  onChange: (v: {
                    target: { value: SetStateAction<string> };
                  }) => itemData.onChange(v.target.value, rowData.type),
                }}
              />
            ) : (
              toPrice(defaultValue)
            );
          case "IMP":
            let taxExists = Object.values(taxesList).some(
              (e: any) =>
                ["string", "number"].includes(typeof defaultValue) &&
                e.TARIMP === Number(defaultValue),
            );
            let totalTaxes = taxesList.map((e: stdObject) => {
              return { label: `${e.TARIMP}%`, value: e.TARIMP };
            });
            return docActive.items ? (
              <WriteInput
                data={{
                  type: "select",
                  value: defaultValue,
                  active: user[`${type}IMPUS1`],
                  inputStyle: rowData.inputStyle,
                  options: taxExists
                    ? totalTaxes
                    : [
                        {
                          label: defaultValue + "%",
                          value: defaultValue,
                        },
                        ...totalTaxes,
                      ],
                  onChange: (v: {
                    target: { value: SetStateAction<string> };
                  }) => itemData.onChange(Number(v.target.value), rowData.type),
                }}
              />
            ) : (
              `${defaultValue}%`
            );
          case "TOT":
            defaultValue = toPrice(defaultValue);
            break;
          case "DES":
          case "CAN":
            const inputType = rowData.type === "CAN" ? "integer" : "number";
            return docActive.items ? (
              <WriteInput
                data={{
                  type: inputType,
                  value: defaultValue,
                  active:
                    type === "TRA" && info.isTransferIn
                      ? false
                      : (rowData.type === "DES" && user[`${type}DESUS1`]) ||
                        rowData.type === "CAN",
                  onChange: (v: {
                    target: { value: SetStateAction<string> };
                  }) => {
                    let value: string | number =
                      typeof v.target.value === "string" ? v.target.value : "0";
                    if (inputType === "integer") {
                      value = parseInt(value);
                    }
                    itemData.onChange(Number(value), rowData.type);
                  },
                  inputStyle: { width: rowData.inputWidth },
                  innerStyle: { textAlign: "right" },
                }}
              />
            ) : (
              defaultValue
            );
        }
        return <span>{defaultValue}</span>;
    }
  };

  const checkIfOk = (rowType: string) => {
    const fieldsRemoved =
      !(type in fieldNotAllowed) ||
      (type in fieldNotAllowed && !fieldNotAllowed[type].includes(rowType));

    if (type === "TRA" && rowType === "DEL" && info.isTransferIn) return false;

    return (
      (docActive.items && fieldsRemoved) ||
      (!docActive.items && !["MORE", "DEL"].includes(rowType) && fieldsRemoved)
    );
  };

  const writeBgColor = (e: stdObject, index: number) => {
    let costOverrun = false;
    if (user.PCO) {
      const suffix = Object.keys(e)[0].slice(-3);
      if (`CAN${suffix}` in e && `PCO${suffix}` in e && `PRE${suffix}` in e) {
        costOverrun =
          e[`CAN${suffix}`] * e[`PCO${suffix}`] >
          e[`CAN${suffix}`] * e[`PRE${suffix}`];
      }
    }
    return costOverrun
      ? colors.pink
      : index % 2
        ? colors.white
        : colors.lightGrey;
  };

  return (
    <div style={{ justifyItems: "center" }}>
      <div style={{ overflowX: "auto", width: "100%" }}>
        <table width="100%" border={0} cellPadding={3} cellSpacing={0}>
          <thead>
            <tr className="header noSelect">
              {itemsCell
                .filter(
                  (e: stdObject) =>
                    !e.hidden &&
                    (!minModalWidth ||
                      (minModalWidth && (e.line2 || e.rowspan))) &&
                    checkIfOk(e.type),
                )
                .map((e: stdObject, index: number) => (
                  <th
                    key={index}
                    style={{
                      backgroundColor: mainColor,
                      width: e.width ?? null,
                    }}
                  >
                    {e.label && t(e.label)}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {items.map((e: stdObject, index: number) =>
              minModalWidth ? (
                <>
                  <tr
                    style={{
                      backgroundColor: writeBgColor(e, index),
                    }}
                  >
                    {itemsCell
                      .filter(
                        (v: stdObject) =>
                          !v.hidden && !v.line2 && checkIfOk(v.type),
                      )
                      .map((v: stdObject, idx: number) => (
                        <td
                          key={idx}
                          style={{ position: "relative" }}
                          rowSpan={v.rowspan ?? 1}
                          colSpan={
                            v.colspan
                              ? itemsCell.filter(
                                  (v: stdObject) =>
                                    !v.hidden && v.line2 && checkIfOk(v.type),
                                ).length
                              : 1
                          }
                          align={v.align ?? "left"}
                        >
                          {writeContent(v, e, index)}
                        </td>
                      ))}
                  </tr>
                  <tr
                    style={{
                      backgroundColor: writeBgColor(e, index),
                    }}
                  >
                    {itemsCell
                      .filter(
                        (v: stdObject) =>
                          !v.hidden && v.line2 && checkIfOk(v.type),
                      )
                      .map((v: stdObject, idx: number) => (
                        <td
                          style={{ position: "relative" }}
                          key={idx}
                          align={v.align ?? "left"}
                        >
                          {writeContent(v, e, index)}
                        </td>
                      ))}
                  </tr>
                </>
              ) : (
                <tr
                  key={index}
                  style={{
                    backgroundColor: writeBgColor(e, index),
                  }}
                >
                  {itemsCell
                    .filter((v: stdObject) => !v.hidden && checkIfOk(v.type))
                    .map((v: stdObject, idx: number) => (
                      <td
                        style={{ position: "relative" }}
                        key={idx}
                        align={v.align ?? "left"}
                      >
                        {writeContent(v, e, index)}
                      </td>
                    ))}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      {docActive.items && !info.isTransferIn && (
        <div
          style={{
            position: "relative",
            backgroundColor: colors.lightGrey,
            borderTop: "solid 4px " + colors.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 8,
            width: "100%",
          }}
        >
          <WriteInput
            data={{
              refId: searchRef,
              type: "text",
              value: inputCode,
              placeholder: t("p.itemCodeEAN"),
              onChange: (v: { target: { value: SetStateAction<string> } }) => {
                getPosition();
                setInputCode(v.target.value);
              },
              onKeyDown: (e: stdObject) => {
                switch (e.key) {
                  case "Enter":
                    if (resList.length > 0) {
                      setResList([]);
                      updateDocData(resList[selectPos], "ART");
                      cleanInput();
                    } else if (insertNewItem(inputCode)) {
                      setInputCode("");
                    }
                    break;
                  case "ArrowUp":
                    if (resList.length > 1) {
                      if (selectPos > 0) {
                        setSelectPos(selectPos - 1);
                      } else {
                        setSelectPos(resList.length - 1);
                      }
                    }
                    break;
                  case "ArrowDown":
                    if (resList.length > 1) {
                      if (selectPos < resList.length - 1) {
                        setSelectPos(selectPos + 1);
                      } else {
                        setSelectPos(0);
                      }
                    }
                    break;
                  case "Escape":
                    cleanInput();
                    break;
                }
              },
              onBlur: () => setTimeout(() => setInputCode(""), 500),
              inputStyle: { width: 180 },
            }}
          />
          <SearchIcon
            onClick={() => insertNewItem(inputCode, true)}
            style={{ color: colors.darkGrey, cursor: "pointer" }}
          />
          <QuickSearch
            type={type}
            value={inputCode}
            resList={resList}
            setResList={setResList}
            isDown={isDown}
            selectPos={selectPos}
            setSelectPos={setSelectPos}
            cleanInput={cleanInput}
          />
        </div>
      )}
    </div>
  );
}
